import React, { forwardRef, useCallback, useRef, useState } from 'react';
import {
  Checkmark,
  Exit,
  Search,
  VisibilityOff,
  VisibilityOn,
} from '@bloomberg/media-ui-icons';

import { HelperText } from '../HelperText';
import { useSlug } from '../hooks/useSlug';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import { InputLabel } from '../InputLabel';
import { GlobalProps, InputProps } from '../types';
import { classes, createId, slugify } from '../utils';

import styles from './outlinedInput.module.css';

export type OutlinedInputFieldProps = GlobalProps & {
  success?: boolean;
  className?: string;
  disabled?: boolean;
  disableHelperText?: boolean;
  error?: boolean;
  helperText?: string;
  id?: string;
  inputProps?: InputProps<HTMLInputElement>;
  labelProps?: InputProps<HTMLLabelElement>;
  label?: string;
  hideLabel?: boolean;
  name?: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  size?: 'extraSmall' | 'small' | 'default';
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'password'
    | 'search'
    | 'tel'
    | 'url'
    | 'date';
  defaultValue?: string;
  value?: string;
  variant?: 'default' | 'dark' | 'markets' | 'superdark';
  onClear?: () => void;
  autocomplete?: string;
};

export const OutlinedInput = forwardRef<
  HTMLDivElement,
  OutlinedInputFieldProps
>(function OutlinedInputComponent(props, ref) {
  const {
    success = false,
    disabled,
    disableHelperText = false,
    error = false,
    helperText,
    hideLabel = false,
    id: userProvidedId,
    inputProps = {},
    labelProps = {},
    label = '',
    name: userProvidedName,
    onChange,
    placeholder = '',
    required,
    size = 'default',
    type,
    defaultValue,
    value,
    unstyled,
    variant,
    onClear,
    autocomplete,
    className,
  } = props;

  const labelText = label;

  const name = userProvidedName || 'text-input';
  const [slug] = useSlug(name);
  const stableId = useRef(createId());

  // inputProps defined values take precedence
  const requestedInputType = inputProps?.type || type;

  // create a unique id for inputs
  const id =
    userProvidedId ||
    inputProps?.id ||
    userProvidedName ||
    label ||
    stableId.current;

  // is the field currently active/focused
  const [active, setActive] = useState<boolean>(false);

  // should password be visible
  const [pwVisible, setPwVisible] = useState<boolean>(false);

  const [focused, setFocused] = useState(false);
  // does the field have input
  const hasValue = Boolean(value?.length || defaultValue?.length || false);

  const currentValue = {
    ...(value !== 'undefined' && {
      value,
    }),
    ...(defaultValue !== 'undefined' && {
      defaultValue,
    }),
  };

  // if cursor is dropped into field, a value exists, or a placeholder is provided
  // render the label in a shrink state
  const shrinkLabel =
    active ||
    hasValue ||
    Boolean(currentValue.value?.toString().length) ||
    Boolean(currentValue.defaultValue?.toString().length);

  const handleActiveChange = useCallback(
    (isActive: boolean) => {
      setActive(isActive);
    },
    [active]
  );

  const containerClasses = classes(
    [
      // base wrapper
      styles.inputContainer,
      type === 'search' && styles.searchBar,
      // size 'small' | 'default' class
      disabled && styles.disabled,
      variant === 'superdark' && styles.superdark,
      styles[size],
      // type
      {
        ...(type && {
          [styles[type]]: true,
        }),
      },
    ],
    props
  );

  const labelClasses = classes(
    [
      'label',
      {
        [styles.error]:
          error && (variant === 'default' || variant === 'superdark'),
        [styles.errorDark]: error && variant === 'dark',
        [styles.labelActive]: active,
      },
    ],
    {
      unstyled: props.unstyled,
      name: `${name}-label`,
    }
  );

  const inputClasses = classes(
    [
      // if custom styles are passed into input
      // use those before defaults
      styles.input,
      {
        [styles.error]:
          error && (variant === 'default' || variant === 'superdark'),
        [styles.errorDark]: error && variant === 'dark',
        [styles.superdark]: variant === 'superdark',
        [styles.hasLabel]: label && !hideLabel,
        [styles.hideLabel]: hideLabel,
        [styles.labelShrink]: shrinkLabel,
        [styles.disabled]: disabled,
        [styles.disabledButShowPlaceholder]:
          disabled && (hideLabel || label === ''),
        // input field marked active
        [styles.active]: active || Boolean(currentValue?.toString.length),
      },
      requestedInputType === 'password' ? styles.password : '',
    ],
    { unstyled: props.unstyled, name: `${name}-input` }
  );

  const iconClasses = classes([styles.icon, styles[size]], {
    unstyled: props.unstyled,
    name: `${name}-icon`,
  });

  // call upstream change handlers
  const handleChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      ev.persist();

      if (inputProps.onChange) {
        inputProps.onChange(ev);
      } else if (onChange) {
        onChange(ev);
      }
    },
    [inputProps, onChange]
  );

  const handleTogglePW = useCallback(() => {
    setPwVisible((v) => !v);
  }, [pwVisible]);

  const handleFocus = useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      ev.persist();
      if (inputProps.onFocus) {
        inputProps.onFocus(ev);
      }
      setFocused(true);
    },
    [inputProps]
  );

  const handleBlur = useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      ev.persist();
      setFocused(false);
      if (inputProps.onBlur) {
        inputProps.onBlur(ev);
      }
    },
    [inputProps]
  );

  // input type should be user defined EXCEPT
  // for when field is password and the user
  // opts in to "show password", in which case
  // we flip the input type to text
  const inputType =
    requestedInputType === 'password' && pwVisible
      ? 'text'
      : requestedInputType;

  return (
    <div
      ref={ref}
      data-component={slug}
      className={classes([
        className,
        styles.wrapper,
        'css--input-base',
        'css--input-outlined-base',
        `${slugify(name)}`,
      ])}
    >
      <div className={containerClasses}>
        <InputLabel
          htmlFor={id}
          className={classes(
            [
              styles.label,
              styles[size],
              {
                [styles.labelShrink]: shrinkLabel,
                [styles.hideLabel]: hideLabel,
                [styles.disabled]: disabled,
                [styles.superdark]: variant === 'superdark',
                [styles.error]:
                  error && (variant === 'default' || variant === 'superdark'),
                [styles.errorDark]: error && variant === 'dark',
                [styles.focused]: active,
                [styles.active]: hasValue && !focused,
                [styles.inactive]: !active && !shrinkLabel,
              },
            ],
            {
              unstyled: props.unstyled,
              name: `${name}-label-outer`,
            }
          )}
          spanClassName={labelClasses}
          label={labelText}
          unstyled={unstyled}
          labelProps={labelProps}
        />
        {type === 'search' && (
          <Search
            className={classes([
              styles.searchIcon,
              disabled && styles.disabled,
              styles[size],
            ])}
          />
        )}
        <Input
          inputProps={{
            ...inputProps,
            onFocus: handleFocus,
            onBlur: handleBlur,
            type: inputType,
            'aria-required': required,
          }}
          {...currentValue}
          id={id}
          type={inputType}
          className={inputClasses}
          disabled={disabled}
          required={required}
          name={name}
          placeholder={placeholder}
          onActiveStatusChange={handleActiveChange}
          onChange={handleChange}
          unstyled={unstyled}
          autoComplete={autocomplete}
        />
        {(type === 'password' || inputProps?.type === 'password') && (
          <span
            className={classes([
              iconClasses,
              { [styles.extraDimension]: success && !error },
            ])}
          >
            <IconButton
              type="button"
              size="small"
              onClick={handleTogglePW}
              title={pwVisible ? 'Hide Password' : 'Show Password'}
            >
              {pwVisible ? <VisibilityOn /> : <VisibilityOff />}
            </IconButton>
          </span>
        )}

        {type === 'search' && onClear && hasValue && (
          <span className={iconClasses}>
            <IconButton
              type="button"
              size="small"
              onClick={onClear}
              title="Clear Input"
            >
              <Exit />
            </IconButton>
          </span>
        )}

        {success && !error && (
          <span
            className={classes([
              iconClasses,
              { [styles.extraPadding]: type === 'password' },
            ])}
          >
            <span
              className={classes([
                styles.successIconContainer,
                {
                  [styles.successIconContainerSuperDark]:
                    variant === 'superdark',
                },
              ])}
            >
              <Checkmark />
            </span>
          </span>
        )}
        {/* Render a fieldset to render a label into */}
        <fieldset
          className={classes([
            styles.fieldset,
            {
              [styles.focused]: focused,
              [styles.disabled]: disabled,
              [styles.active]: hasValue && !focused,
              [styles.error]:
                error && (variant === 'default' || variant === 'superdark'),
              [styles.errorDark]: error && variant === 'dark',
              [styles.fieldsetActive]: shrinkLabel,
              [styles.markets]: variant === 'markets',
              [styles.superdark]: variant === 'superdark',
            },
          ])}
        >
          {label && (
            <legend
              className={classes([
                styles.legend,
                {
                  [styles.legendActive]: shrinkLabel,
                  [styles.disabled]: disabled,
                  [styles.error]: error && variant === 'default',
                  [styles.errorDark]: error && variant === 'dark',
                  [styles.hideLabel]: hideLabel,
                },
              ])}
            >
              {label}
            </legend>
          )}
        </fieldset>
      </div>
      {!disableHelperText && (
        <HelperText
          className={classes(styles.helperText)}
          error={error}
          text={helperText}
          variant={
            variant === 'dark' || variant === 'superdark' ? 'dark' : 'default'
          }
        />
      )}
    </div>
  );
});
